
import { useEffect, useState, useCallback } from "react";
import { UnifiedTransactionForm } from "@/components/transactions/UnifiedTransactionForm";
import { useTransactions } from "@/hooks/useTransactions";
import { useActionLogger } from "@/services/actionLogger";
import { useNotifications } from "@/services/notificationService";
import type { RecurrencePattern, Transaction, TransactionFormData, TransactionType } from "@/types/transaction";
import type { FileWithDescription } from "@/hooks/storage/useFileOperations";
import type { TransactionSchema } from "@/components/transactions/schema/transaction-schema";
import type { UnifiedTransactionSchema } from "@/components/transactions/UnifiedTransactionForm";
import { fromDateString } from "@/lib/utils/dateUtils";

interface TransactionFormContainerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  transactionId: string | null;
  onClose: () => void;
  initialTransaction?: Transaction | null; // optional prefill for clone/create
}

// Helper: map DB Transaction -> TransactionScreen schema
function mapDbToScreen(tx: Transaction) {
  const toISODate = (d: Date | string | undefined | null) => {
    if (!d) return "";
    if (typeof d === "string") {
      // If it's already a YYYY-MM-DD string, return it directly
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
      // If it's an ISO string, take the date part
      if (d.includes("T")) return d.split("T")[0];
    }
    // Fallback for Date objects or other formats
    const dateObj = typeof d === "string" ? new Date(d) : (d as Date);
    // Use local date formatting instead of toISOString (which is UTC)
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    id: tx.id, // ✅ ADICIONANDO O ID DA TRANSAÇÃO
    type: tx.type === "income" ? "INCOME" : "EXPENSE",
    description: tx.description || "",
    amount: tx.amount || 0,
    date: toISODate(tx.date),
    // Prioriza due_date; fallback para previsao (date); por fim payment_date (compat)
    dueDate: toISODate(tx.due_date || tx.date || tx.payment_date),
    competence_date: toISODate(tx.competence_date),
    categoryId: tx.category_id ? String(tx.category_id) : "",
    subcategoryId: "",
    accountId: tx.payment_account_id ? String(tx.payment_account_id) : "",
    costCenterId: tx.cost_center_id ? String(tx.cost_center_id) : "",
    supplier_id: tx.supplier_id || undefined,
    client_id: tx.client_id || undefined,
    contactId: tx.type === "expense" ? (tx.supplier_id || "") : (tx.client_id || ""),
    paymentMethod: tx.payment_method || "",
    installmentCount: tx.total_installments || 1,
    installmentType: tx.is_recurring ? "RECURRING" : (tx.total_installments && tx.total_installments > 1) ? "INSTALLMENTS" : "CASH",
    tags: [],
    attachments: [],
    notes: tx.notes || "",
    status: tx.status || (tx.is_paid ? "PAID" : "PENDING"),
    isPaid: tx.is_paid || false,
    isRecurring: !!tx.is_recurring,
    recurrenceFrequency: tx.recurrence_pattern
      ? tx.recurrence_pattern.toUpperCase() as "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY"
      : undefined,
    recurrenceInterval: tx.recurrence_interval ?? undefined,
    recurrenceCount: tx.recurrence_count ?? undefined,
    recurrenceEndDate: tx.recurrence_end_date ? toISODate(tx.recurrence_end_date) : undefined,
  } as UnifiedTransactionSchema;
}

// Helper: map TransactionScreen schema -> Edit/Create payload
function mapScreenToMutationPayload(data: TransactionSchema): TransactionFormData {
  // Usar fromDateString para evitar problemas de timezone
  // new Date("YYYY-MM-DD") interpreta como UTC, causando shift de dia
  const toDate = (s: string | undefined): Date | undefined => {
    if (!s) return undefined;
    try {
      return fromDateString(s);
    } catch {
      // Fallback para strings não padronizadas
      return new Date(s);
    }
  };
  const isIncome = data.type === "INCOME";

  const normalizeId = (value: string | null | undefined) => {
    if (value === undefined || value === null) return undefined;
    const trimmed = String(value).trim();
    return trimmed.length > 0 ? trimmed : undefined;
  };

  const safeAmount = (value: string | number | undefined) => {
    const n = Number(value);
    return value !== undefined && value !== null && value !== "" && !Number.isNaN(n) ? n : undefined;
  };

  console.log("📋 mapScreenToMutationPayload - Entrada:", {
    categoryId: data.categoryId,
    categoryIdType: typeof data.categoryId,
    accountId: data.accountId,
    description: data.description,
  });

  const categoryIdNumber = data.categoryId ? Number(data.categoryId) : undefined;

  console.log("🔢 Conversão de categoria:", {
    original: data.categoryId,
    converted: categoryIdNumber,
    isValid: categoryIdNumber !== undefined && !Number.isNaN(categoryIdNumber)
  });

  const payload: TransactionFormData = {
    type: (isIncome ? "income" : "expense") as TransactionType,
    amount: safeAmount(data.amount),
    description: data.description,
    category_id: categoryIdNumber !== undefined && !Number.isNaN(categoryIdNumber) ? categoryIdNumber : undefined,
    payment_account_id: data.accountId ? normalizeId(data.accountId) : undefined,
    cost_center_id: data.costCenterId ? Number(data.costCenterId) : undefined,
    date: toDate(data.date) as Date,
    competence_date: toDate(data.competence_date),
    // Previsão de pagamento (vencimento)
    due_date: toDate(data.dueDate),
    is_paid: data.status === "PAID",
    notes: data.notes || "",
    // Mapear installmentType para total_installments e is_recurring
    total_installments: (data as any).installmentType === "INSTALLMENTS"
      ? ((data as any).total_installments || 2)
      : 1,
    is_recurring: (data as any).installmentType === "RECURRING",
    payment_method: data.paymentMethod || undefined,
    // Data de pagamento efetivo (se houver)
    payment_date: toDate((data as any).payment_date),
    // Priorizar novos campos, com fallback para compatibilidade
    // Usar os campos UUID corretos diretamente
    supplier_id: normalizeId(data.supplier_id),
    client_id: normalizeId(data.client_id),
  };

  // Campos de recorrência (são preenchidos pelo PaymentConditions)
  payload.recurrence_pattern = data.recurrenceFrequency
    ? data.recurrenceFrequency.toLowerCase() as RecurrencePattern
    : undefined;
  payload.recurrence_interval = data.recurrenceInterval ?? undefined;
  payload.recurrence_count = data.recurrenceCount ?? undefined;
  payload.recurrence_end_date = data.recurrenceEndDate ? toDate(data.recurrenceEndDate) : undefined;

  console.log("✅ Payload final:", {
    category_id: payload.category_id,
    payment_account_id: payload.payment_account_id,
    description: payload.description,
    total_installments: payload.total_installments,
    payment_method: payload.payment_method // EXPLICIT CHECK
  });

  return payload;
}

export function TransactionFormContainer({
  isOpen,
  onOpenChange,
  transactionId,
  onClose,
  initialTransaction,
}: TransactionFormContainerProps) {
  const { getTransactionById, editTransaction, createTransaction } = useTransactions();
  const { logger, logPerformance } = useActionLogger();
  const { triggerNotification } = useNotifications();
  const [screenTransaction, setScreenTransaction] = useState<any | null>(null);
  const [originalTransaction, setOriginalTransaction] = useState<Transaction | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Detect if transaction is an installment child
  const isInstallmentChild = !!originalTransaction?.parent_transaction_id;
  const installmentInfo = isInstallmentChild ? {
    current: originalTransaction.current_installment ?? 1,
    total: originalTransaction.total_installments ?? 1,
    parentId: originalTransaction.parent_transaction_id ?? ''
  } : null;

  // Prefill from initialTransaction for clone/create
  useEffect(() => {
    if (!transactionId && initialTransaction) {
      const mapped = mapDbToScreen(initialTransaction as any);
      // Reset fields that must not be carried over
      mapped.id = undefined as any;
      mapped.isPaid = false;
      // Bring attachments for clone (existing ones)
      (mapped as any).attachments = [];
      (mapped as any).existingAttachments = (initialTransaction as any).attachments || [];
      setScreenTransaction(mapped);
      setOriginalTransaction(null);
    }
  }, [transactionId, initialTransaction]);

  // Load transaction when editing
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!transactionId) {
        setScreenTransaction(null);
        setOriginalTransaction(null);
        return;
      }
      setIsLoading(true);
      try {
        const tx = await getTransactionById(transactionId);
        if (!cancelled) {
          const mappedTx = mapDbToScreen(tx);
                    setScreenTransaction(mappedTx);
          setOriginalTransaction(tx);
        }
      } catch (err) {
        console.error("Erro ao carregar transação para edição:", err);
        setScreenTransaction(null);
        setOriginalTransaction(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [transactionId, getTransactionById]);

  const handleSave = useCallback(
    async (data: TransactionSchema) => {
      const startTime = Date.now();

      // Quando editando, preservar valores existentes para campos não preenchidos
      const sourceData = transactionId && screenTransaction
        ? { ...screenTransaction, ...data }
        : data;

      const {
        attachmentFiles: rawAttachmentFiles = [],
        existingAttachmentUpdates: rawExistingAttachmentUpdates = [],
        ...normalizedData
      } = sourceData;

      const attachmentsForMutation: FileWithDescription[] = Array.isArray(rawAttachmentFiles)
        ? rawAttachmentFiles
          .filter((item: unknown): item is { file: File; description?: string; attachment_form?: any; attachment_type?: any } =>
            typeof item === 'object' && item !== null && 'file' in item && item.file instanceof File)
          .map((item) => ({
            file: item.file,
            description: item.description || '',
            attachment_form: item.attachment_form,
            attachment_type: item.attachment_type,
          }))
        : [];

      const existingAttachmentUpdates = Array.isArray(rawExistingAttachmentUpdates)
        ? rawExistingAttachmentUpdates
          .filter((item: unknown): item is { id: number; description?: string | null } =>
            typeof item === 'object' && item !== null && 'id' in item && typeof item.id === 'number')
          .map((item) => ({
            id: item.id,
            description: item.description ?? null,
          }))
        : [];

      const payload = mapScreenToMutationPayload(normalizedData);

      try {
        // Log performance start
        logPerformance('transaction_save_start', startTime);

        if (transactionId) {
          
          // Regular single transaction edit
          await editTransaction.mutateAsync({
            id: transactionId,
            formData: {
              ...payload,
              attachments: attachmentsForMutation.length > 0 ? attachmentsForMutation : undefined,
              existingAttachments: existingAttachmentUpdates.length > 0 ? existingAttachmentUpdates : undefined,
            },
            previousTransaction: originalTransaction || undefined,
          });
          logger.success('Transaction Updated', `Transaction ${normalizedData.description} updated successfully`, {
            category: 'transaction',
            details: {
              transactionId,
              transactionType: payload.type as string,
              amount: typeof payload.amount === 'number' ? payload.amount : undefined,
            },
          });
        } else {
          const result = await createTransaction.mutateAsync({
            ...payload,
            attachments: attachmentsForMutation.length > 0 ? attachmentsForMutation : undefined,
          } as TransactionFormData & { attachments?: FileWithDescription[] });
          logger.success('Transaction Created', `Transaction ${normalizedData.description} created successfully`, {
            category: 'transaction',
            details: {
              transactionId: String(result?.id ?? ''),
              transactionType: payload.type as string,
              amount: typeof payload.amount === 'number' ? payload.amount : undefined,
            },
          });
        }

        // Log performance
        logPerformance('transaction_save_complete', startTime);

        // Trigger notifications
        if (transactionId) {
          // Notification removed to avoid duplicate toasts (TransactionScreen shows manual toast with Undo button)
          // void triggerNotification('transaction_updated', { ...normalizedData, id: transactionId })
          //   .catch((error) => {
          //     console.warn('Notification dispatch failed:', error);
          //   });
        } else {
          // Notification removed to avoid duplicate toasts (TransactionScreen shows manual toast with Undo button)
          // void triggerNotification('transaction_created', normalizedData)
          //   .catch((error) => {
          //     console.warn('Notification dispatch failed:', error);
          //   });
        }

        onClose();
        onOpenChange(false);
      } catch (err) {
        logger.logTransactionError(transactionId ? 'update' : 'create', transactionId, err);
        console.error("Erro ao salvar transação:", err);
        throw err; // Re-throw to let TransactionScreen handle it
      }
    },
    [transactionId, screenTransaction, editTransaction, createTransaction, onClose, onOpenChange, logger, logPerformance, triggerNotification, originalTransaction]
  );

  const handleClose = () => {
    setScreenTransaction(null);
    setOriginalTransaction(null);
  
    // Limpar estado da transação ao fechar
    setScreenTransaction(null);
    onClose();
    onOpenChange(false);
  };

  return (
    <UnifiedTransactionForm
      isOpen={isOpen}
      onClose={handleClose}
      onSave={async (data: UnifiedTransactionSchema & {
        attachmentFiles?: { file: File; description: string; attachment_form?: any; attachment_type?: any }[];
        existingAttachmentUpdates?: { id: number; description?: string | null }[];
      }) => {
        // Convert UnifiedTransactionSchema to TransactionSchema format
        const transactionSchemaData: TransactionSchema = {
          ...data,
          // Map back to TransactionScreen format
          contactId: data.type === "INCOME" ? (data.client_id || data.contactId || "") : (data.supplier_id || data.contactId || ""),
          attachmentFiles: data.attachmentFiles || [],
          existingAttachmentUpdates: data.existingAttachmentUpdates || [],
        };

        await handleSave(transactionSchemaData);
      }}
      transaction={screenTransaction || undefined}
      isEditing={!!transactionId}
      isInstallmentChild={isInstallmentChild}
      installmentInfo={installmentInfo}
    />
  );
}
